import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CreditCard,
  Lock,
  Minus,
  Plus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { useToast } from "../components/ui/use-toast";
import { EventService, DynamicModuleService } from "../services/APIs";
import type { Event } from "../utils/types/event";

interface TierData {
  id: string;
  name: string;
  price: number;
  currency: string;
  type: "paid" | "free";
}

const EventPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("id");
  const tierId = searchParams.get("tier");
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [eventLoading, setEventLoading] = useState<boolean>(true);
  const [tierData, setTierData] = useState<TierData | null>(null);
  const [tierLoading, setTierLoading] = useState<boolean>(true);
  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Fetch event details
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setEventLoading(false);
        return;
      }

      try {
        setEventLoading(true);
        const details = await EventService.getEventDetails(eventId, false);
        if (details) {
          setEvent(details);
        } else {
          const allEvents = await EventService.getAllEvents(false);
          const found = allEvents.find(e => e.id === eventId);
          if (found) {
            setEvent(found);
          } else {
            const single = await EventService.getEventById(eventId, false);
            setEvent(single);
          }
        }
      } catch (error) {
        console.error("Error fetching event for payment:", error);
        setEvent(null);
      } finally {
        setEventLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Fetch specific ticket (tier) data using DynamicModuleService
  useEffect(() => {
    const fetchTier = async () => {
      if (!eventId || !tierId) {
        setTierLoading(false);
        return;
      }

      try {
        setTierLoading(true);
        const response = await DynamicModuleService.getGridCoByConfig(
          "Add Ticket",
          1,
          100,
          ["id", "category", "price"],
          { jdid: eventId }
        );

        let tickets: any[] = [];
        if (response?.results && Array.isArray(response.results)) {
          tickets = response.results;
        } else if (Array.isArray(response)) {
          tickets = response;
        }

        const ticket = tickets.find(t => t.id === tierId);
        if (ticket) {
          setTierData({
            id: ticket.id,
            name: ticket.category ?? "Ticket",
            price: ticket.price ? parseFloat(ticket.price) || 0 : 0,
            currency: "USD",
            type: "paid",
          });
        } else {
          setTierData(null);
        }
      } catch (error) {
        console.error("Error fetching ticket for payment:", error);
        setTierData(null);
      } finally {
        setTierLoading(false);
      }
    };

    fetchTier();
  }, [eventId, tierId]);

  const subtotal = (tierData?.price ?? 0) * quantity;
  // const processingFee = subtotal * 0.029 + 0.3; // Typical US payment processing fee
  const total = subtotal;

  const handleQuantityChange = (increment: boolean) => {
    setQuantity(prev => {
      if (increment) {
        return Math.min(prev + 1, 10);
      }
      return Math.max(prev - 1, 1);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!fullName.trim()) {
      toast({
        title: "Validation Error",
        description: "Full Name is required",
        variant: "destructive",
      });
      return;
    }

    if (!email.trim()) {
      toast({
        title: "Validation Error",
        description: "Email is required",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!phone.trim()) {
      toast({
        title: "Validation Error",
        description: "Phone number is required",
        variant: "destructive",
      });
      return;
    }

    if (!eventId || !tierData) {
      toast({
        title: "Error",
        description: "Event or ticket information is missing",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Calculate totals (matching BuyTicketPage logic)
      const subtotal = (tierData.price ?? 0) * quantity;
      const fees = 0; // 3% fees - set to 0
      const paymentGatewayCharges = 0; // Payment Gateway Charges - set to 0
      const discount = 0; // No promo code in this flow
      const grandTotal = subtotal + fees + paymentGatewayCharges - discount;

      // Prepare ticket items array (matching BuyTicketPage structure)
      const ticketItems = [
        {
          ticket_id: tierData.id,
          no_of_ticket: quantity,
          price: tierData.price.toFixed(2),
          amount: (tierData.price * quantity).toFixed(2),
        },
      ];

      // Prepare payload (matching BuyTicketPage structure)
      const payload = {
        eventid: eventId,
        total_amount: grandTotal.toFixed(2),
        fullname: fullName.trim(),
        email: email.trim(),
        mobile: phone.trim(),
        tickets: ticketItems,
      };

      // Call the API (same as BuyTicketPage)
      const response = await EventService.buyTickets(payload, true);

      if (response) {
        toast({
          title: "Payment Successful!",
          description: `You've successfully purchased ${quantity} ticket(s)${tierData ? ` for ${tierData.name}` : ""
            }.`,
        });
        if (eventId) {
          navigate(CitycollabNavigationRoutes.EVENTS);
          navigate(CitycollabNavigationRoutes.EVENTS);
        }
      }
    } catch (error) {
      console.error("Error buying tickets:", error);
      toast({
        title: "Payment Failed",
        description:
          "There was an error processing your payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Complete Your Purchase</h1>
              <p className="text-sm text-muted-foreground">
                Secure checkout powered by trusted payment providers
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Attendee Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Attendee Information</CardTitle>
                  <CardDescription>
                    Primary contact for this registration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      placeholder="John Doe"
                      required
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      required
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" placeholder="San Francisco" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Select required>
                        <SelectTrigger id="state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AL">Alabama</SelectItem>
                          <SelectItem value="AK">Alaska</SelectItem>
                          <SelectItem value="AZ">Arizona</SelectItem>
                          <SelectItem value="CA">California</SelectItem>
                          <SelectItem value="FL">Florida</SelectItem>
                          <SelectItem value="NY">New York</SelectItem>
                          <SelectItem value="TX">Texas</SelectItem>
                          {/* Add more states as needed */}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      placeholder="94102"
                      required
                      maxLength={5}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Payment Information
                  </CardTitle>
                  <CardDescription>
                    Your payment information is secure and encrypted
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="cardNumber">Card Number *</Label>
                    <div className="relative">
                      <Input
                        id="cardNumber"
                        placeholder="1234 5678 9012 3456"
                        required
                        maxLength={19}
                      />
                      <CreditCard className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cardName">Name on Card *</Label>
                    <Input id="cardName" placeholder="John Doe" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expiry">Expiry Date *</Label>
                      <Input
                        id="expiry"
                        placeholder="MM/YY"
                        required
                        maxLength={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cvv">CVV *</Label>
                      <Input
                        id="cvv"
                        placeholder="123"
                        required
                        maxLength={4}
                        type="password"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billingZip">Billing ZIP Code *</Label>
                    <Input
                      id="billingZip"
                      placeholder="94102"
                      required
                      maxLength={5}
                    />
                  </div>
                </CardContent>
              </Card>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={isProcessing}
              >
                {isProcessing
                  ? "Processing..."
                  : `Pay $${total.toFixed(2)} USD`}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                By completing this purchase, you agree to the event terms and
                conditions. Your payment is securely processed.
              </p>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Event Details */}
                <div>
                  <h3 className="mb-2 font-semibold">
                    {eventLoading
                      ? "Loading event..."
                      : (event?.event_name ?? "Event")}
                  </h3>
                  {event && (
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {event.start_date
                            ? formatDate(new Date(event.start_date))
                            : "Date not available"}
                        </span>
                      </div>
                      {event.address && <p className="pl-6">{event.address}</p>}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Ticket Selection */}
                <div>
                  {tierLoading || !tierData ? (
                    <div className="text-sm text-muted-foreground">
                      {tierLoading ? "Loading ticket..." : "Ticket not found."}
                    </div>
                  ) : (
                    <>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="font-medium">{tierData.name}</span>
                        <span className="text-sm text-muted-foreground">
                          ${tierData.price.toFixed(2)} each
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(false)}
                          disabled={quantity <= 1}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center text-lg font-semibold">
                          {quantity}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(true)}
                          disabled={quantity >= 10}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <span className="ml-auto text-sm text-muted-foreground">
                          {quantity} {quantity === 1 ? "ticket" : "tickets"}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <Separator />

                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Processing Fee
                    </span>
                    <span>$00.00</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>${total.toFixed(2)} USD</span>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    <span>Secure SSL encrypted payment</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPayment;
